import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "../Style/Password_warning.css";
import logo from "../Images/warning_sign.jpg";
import login_image from "../Images/Login-image.png";
import icons8_google from "../Images/icons8-google.svg";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import axios from "axios";
import { path_url, responseMessages } from "../Config/config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { saveToken, notifyLogin, saveUserInfo } from "../Utils/auth";
import SEO from "../components/SEO";

export default function Login() {
  const [isLogin, SetLogin] = useState(false);
  const navigate = useNavigate();
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [issubmit, setIssubmit] = useState(false);

  const handleGoogleLogin = () => {
    window.location.href = `${path_url}/oauth2/login`;
  };

  const handleKeyDown = (event) => {
    if (event.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    } else {
      setCapsLockOn(false);
    }
  };

  const handleKeyUp = (event) => {
    if (!event.getModifierState("CapsLock")) {
      setCapsLockOn(false);
    }
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema,

    onSubmit: async (values, { setSubmitting, setStatus }) => {
      setIssubmit(true);
      try {
        const event = new Event("submit", { cancelable: true });
        event.preventDefault();
        const response = await axios.post(`${path_url}/authenticate`, values);

        const token =
          response.data.token ||
          response.data.accessToken ||
          response.data.access_token ||
          response.data.jwt;
        saveToken(token);

        // Extract name from auth response — Spring Boot may return it alongside the token.
        // Store it now so UserContext can display it immediately even if /user returns 500.
        const authName =
          response.data.name ||
          response.data.userName ||
          response.data.fullName ||
          response.data.displayName;
        const authEmail = response.data.email || values.username;

        if (authName && !authName.includes("@")) {
          // Auth response returned a real name — save it
          saveUserInfo({ name: authName, email: authEmail });
        } else if (authEmail) {
          // Save at least the email so the context has something to work with
          saveUserInfo({ email: authEmail });
        }

        notifyLogin();

        SetLogin(true);
        toast.success("Login Successfully");
      } catch (error) {
        const errorMessage = error.response?.data?.message
          ? responseMessages[error.response.data.message]
          : "Server is unreachable. Please ensure the backend is running.";
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
        setIssubmit(false);
      }
    },
  });

  useEffect(() => {
    if (isLogin) {
      // UserContext already fetches user info when it receives the entelipro:login event
      // (dispatched by notifyLogin() above). No duplicate /user call needed here.
      const redirectTimer = setTimeout(() => {
        navigate("/crm-dashboard", { replace: true });
      }, 500);

      return () => {
        clearTimeout(redirectTimer);
      };
    }
  }, [isLogin, navigate]);

  return (
    <>
      <SEO
        title="Login to AI ERP & CRM Software | EnteliPro"
        description="Login to EnteliPro AI ERP and CRM software. Access your business management dashboard, manage sales, customer relationships, and more."
        keywords="Login, AI ERP Login, CRM Login, Business Software Login, EnteliPro Login, Enterprise Software Login"
        canonical="https://www.entelipro.com/login"
        ogImage="https://www.entelipro.com/static/media/Login-image.png"
      />

      <Navbar />

      {/* Hidden H1 for SEO - visible to search engines but hidden visually */}
      <h1 className="visually-hidden">
        Login to EnteliPro AI ERP & CRM Software
      </h1>

      <div className="Login-form">
        <div className="login-image-container">
          <img
            src={login_image}
            alt="EnteliPro AI ERP and CRM Software Login Dashboard"
            className="login-image"
          />
        </div>
        <form onSubmit={formik.handleSubmit} className="form-container">
          <div className="contents">
            <span className="Login-heading">
              Welcome to AI Smart ERP Solution
            </span>
            <span className="Login-content">Login to explore more.</span>
          </div>

          <div className="login-form-main-container">
            <div className="login-form-content">
              <div className="form-group">
                <input
                  name="username"
                  type="text"
                  placeholder="Email"
                  className={
                    "form-control form-control-2" +
                    (formik.errors.username && formik.touched.username
                      ? " is-invalid"
                      : "")
                  }
                  onChange={formik.handleChange}
                  value={formik.values.username}
                />
                <div className="invalid-feedback">
                  {formik.errors.username && formik.touched.username
                    ? formik.errors.username
                    : null}
                </div>
              </div>

              <div className="form-group">
                <input
                  name="password"
                  type="password"
                  placeholder="Password"
                  className={
                    "form-control form-control-2" +
                    (formik.errors.password && formik.touched.password
                      ? " is-invalid"
                      : "")
                  }
                  onChange={formik.handleChange}
                  value={formik.values.password}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyUp}
                />
                {capsLockOn && (
                  <div className={"warning"}>
                    <strong className={"h1"}>
                      <img
                        src={logo}
                        style={{ height: "52px", width: "50px" }}
                        alt="Caps Lock Warning"
                      />
                      Caps Lock is On!
                    </strong>
                    <p className={"h2"}>
                      Having Caps Lock on may cause you to enter your password
                      incorrectly.
                    </p>
                    <br></br>
                    <p className={"h2"}>
                      You should press Caps Lock to turn it off before entering
                      your password.
                    </p>
                  </div>
                )}
                <div className="invalid-feedback">
                  {formik.errors.password && formik.touched.password
                    ? formik.errors.password
                    : null}
                </div>
                <div style={{ textAlign: "right", marginTop: "8px" }}>
                  <Link
                    to="/forgot-password"
                    style={{
                      color: "#5e3bee",
                      textDecoration: "none",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>
            </div>

            <div className="buttons buttons-2">
              <div className="form-group">
                <button
                  type="submit"
                  className="login-buttons"
                  disabled={issubmit || formik.isSubmitting}
                  aria-busy={issubmit || formik.isSubmitting}
                >
                  <div className="btn-content login-loading-content">
                    {issubmit ? (
                      <>
                        <span
                          className="login-loading-spinner"
                          aria-hidden="true"
                        ></span>
                        <span>Logging in...</span>
                      </>
                    ) : (
                      <>
                        <span>Log</span>
                        <span> in</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
              <div className="form-group">
                <Link to="/register">
                  <button type="button" className="user-buttons">
                    Create New User
                  </button>
                </Link>
              </div>

              <div className="form-group">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="Google-buttons"
                >
                  <div className="google-icon">
                    <img src={icons8_google} alt="Google Login Icon" />
                  </div>
                  Log in with Google
                </button>
              </div>

              <div className="form-group text-center">
                <span className="register-prompt">
                  New user?{" "}
                  <Link to="/register" className="register-link">
                    Register here.
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>

      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}